import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  IPaginationProps,
  LinearProgress,
  LoadedContent,
} from '@occc/ui-design-system';

import { SegmentFragment } from '@occc/graphql';
import { SegmentActionsContainer } from './SegmentActionsContainer';

import { routeCreator } from '../utils';

type SegmentsTableProps = {
  segments?: SegmentFragment[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  isUserPermittedToEditSegment: boolean;
  isUserPermittedToDeleteSegment: boolean;
};

export const SegmentsTable = ({
  segments,
  loading,
  error,
  paginationProps,
  isUserPermittedToEditSegment,
  isUserPermittedToDeleteSegment,
}: SegmentsTableProps) => {
  const navigate = useNavigate();
  const areActionsAvailable = isUserPermittedToEditSegment || isUserPermittedToDeleteSegment;

  const renderData = (items: SegmentFragment[]) => {
    const rows = items.map((segment) => {
      const onClick = () => navigate(routeCreator.details(segment.uuid));

      return (
        <TableRow key={segment.uuid} onClick={onClick}>
          <TableCell>{segment.name || 'No Name'}</TableCell>
          <TableCell>{segment.description || 'No Description'}</TableCell>
          <TableCell>{segment.shortCode || 'No Short Code'}</TableCell>
          <TableCell>{segment.membershipCount || 0}</TableCell>
          {areActionsAvailable && (
            <TableCell padding='button' align='right'>
              <SegmentActionsContainer
                id={segment.uuid}
                name={segment.name || ''}
                isUserPermittedToEditSegment={isUserPermittedToEditSegment}
                isUserPermittedToDeleteSegment={isUserPermittedToDeleteSegment}
              />
            </TableCell>
          )}
        </TableRow>
      );
    });

    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Short Code</TableCell>
              <TableCell>Size</TableCell>
              {areActionsAvailable && <TableCell align='right'>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<SegmentFragment>
      error={error}
      loading={loading}
      items={segments}
      renderData={renderData}
    />
  );
};
